import { CustomHeader } from "@/components/CustomHeader";
import { PostCard } from "@/components/PostCard";
import { RequestGroupAccess } from "@/components/RequestGroupAccess";
import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import { useGroupAccess } from "@/hooks/useGroupAccess";
import { useMarkGroupViewed } from "@/hooks/useMarkGroupViewed";
import { Group } from "@/types/group";
import { Post } from "@/types/post";
import { getGroupPictureThumbnail } from "@/utils/getGroupPictureThumbnail";
import { useGroupImageOverride } from "@/utils/groupImageOverrides";
import { mapDocToPost } from "@/utils/mapDocToPost.native";
import { cachePosts } from "@/utils/postCache";
import { ActionSheetProvider } from "@expo/react-native-action-sheet";
import Ionicons from "@expo/vector-icons/Ionicons";
import firestore from "@react-native-firebase/firestore";
import { Image } from "expo-image";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  ActivityIndicator,
  FlatList,
  StyleSheet,
  TouchableOpacity,
} from "react-native";
const DefaultGroupImage = require("../../../assets/images/default-group.png");

export default function GroupDetailsScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const router = useRouter();
  const [group, setGroup] = useState<Group | null>(null);
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  const { status: accessStatus, canRead, canPost } = useGroupAccess(id);
  const blockedFromGroup = accessStatus !== "loading" && !canRead;
  const overrideUri = useGroupImageOverride(id);

  useMarkGroupViewed(id);

  useEffect(() => {
    if (!id) return;

    const groupDocRef = firestore().collection("groups").doc(id);
    const unsubscribeGroup = groupDocRef.onSnapshot((doc) => {
      if (doc.exists()) {
        setGroup({ id: doc.id, ...doc.data() } as Group);
      } else {
        // Handle group not found
      }
    });

    const postsQuery = firestore()
      .collection("posts")
      .where("groupId", "==", id)
      .orderBy("createdAt", "desc");
    const unsubscribePosts = postsQuery.onSnapshot((snapshot) => {
      const postsData = snapshot.docs.map((doc) => mapDocToPost(doc));
      setPosts(postsData);
      // Seed the by-id cache so tapping a group post repaints details instantly.
      cachePosts(postsData);
      setLoading(false);
    });

    return () => {
      unsubscribeGroup();
      unsubscribePosts();
    };
  }, [id]);

  return (
    <ActionSheetProvider>
      <ThemedView style={styles.container}>
        <TouchableOpacity
          disabled={!canPost}
          onPress={() =>
            router.push({
              pathname: "/(tabs)/groups/group-info",
              params: { group: JSON.stringify(group) },
            })
          }
        >
          <CustomHeader type="subtitle" showBackButton>
            <ThemedView style={styles.headerContent}>
              <Image
                source={{
                  uri:
                    overrideUri ??
                    getGroupPictureThumbnail(
                      id,
                      group?.photoUpdatedAt?.toMillis?.(),
                    ),
                }}
                style={styles.avatar}
                placeholder={DefaultGroupImage}
                cachePolicy="disk"
                transition={300}
              />
              <ThemedText
                type={"subtitle"}
                numberOfLines={1}
                ellipsizeMode="tail"
              >
                {group?.name ?? ""}
              </ThemedText>
            </ThemedView>
          </CustomHeader>
        </TouchableOpacity>
        {accessStatus === "loading" || (loading && !blockedFromGroup) ? (
          <ThemedView style={styles.centered}>
            <ActivityIndicator />
          </ThemedView>
        ) : blockedFromGroup ? (
          <RequestGroupAccess
            groupId={id}
            groupName={group?.name ?? ""}
            pending={accessStatus === "request-pending"}
          />
        ) : (
          <FlatList
            data={posts}
            keyExtractor={(item) => item.id}
            renderItem={({ item }) => <PostCard post={item} />}
            ListEmptyComponent={() => (
              <ThemedView
                style={{
                  flex: 1,
                  width: "100%",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: 200,
                }}
              >
                <ThemedText>{t("NO_POSTS_IN_GROUP")}</ThemedText>
              </ThemedView>
            )}
          />
        )}
        {canPost && (
          <TouchableOpacity
            style={styles.fab}
            onPress={() =>
              router.push({
                pathname: "/(tabs)/post/new",
                params: { groupId: id, targetName: group?.name || t("GROUPS") },
              })
            }
          >
            <Ionicons name="add" size={30} color="white" />
          </TouchableOpacity>
        )}
      </ThemedView>
    </ActionSheetProvider>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  centered: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  headerContent: {
    flexDirection: "row",
    alignItems: "center",
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    marginRight: 12,
  },
  fab: {
    position: "absolute",
    width: 56,
    height: 56,
    alignItems: "center",
    justifyContent: "center",
    right: 20,
    bottom: 10,
    backgroundColor: "#1e3a8a",
    borderRadius: 30,
    elevation: 8,
    zIndex: 10,
  },
});
